import React, { useEffect, useRef, useState } from "react";
import "./Team2.css";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import {
  faLinkedinIn,
  faFacebookF,
  faInstagram,
} from "@fortawesome/free-brands-svg-icons";

// =========================================
// SOCIAL LINKS
// =========================================

function SocialLinks({ member }) {
  const links = [
    {
      url: member.email ? `mailto:${member.email}` : "",
      icon: faEnvelope,
      label: "Email",
    },
    {
      url: member.linkedin,
      icon: faLinkedinIn,
      label: "LinkedIn",
    },
    {
      url: member.facebook,
      icon: faFacebookF,
      label: "Facebook",
    },
    {
      url: member.instagram,
      icon: faInstagram,
      label: "Instagram",
    },
  ];

  const availableLinks = links.filter((link) => link.url);

  if (availableLinks.length === 0) {
    return null;
  }

  return (
    <div className="team2-social-links">
      {availableLinks.map((link) => (
        <a
          key={link.label}
          href={link.url}
          title={link.label}
          aria-label={`${member.name} - ${link.label}`}
          target={
            link.url.startsWith("mailto:")
              ? undefined
              : "_blank"
          }
          rel={
            link.url.startsWith("mailto:")
              ? undefined
              : "noopener noreferrer"
          }
        >
          <FontAwesomeIcon icon={link.icon} />
        </a>
      ))}
    </div>
  );
}

// =========================================
// TEAM CATEGORY
// =========================================

function TeamRow({ title, members = [], onSelect }) {
  if (!members.length) return null;

  return (
    <div className="team2-category">

      <h3 className="team2-category-title">
        {title}
      </h3>

      <div className="row justify-content-center g-3">

        {members.map((member, index) => (
          <div
            key={`${title}-${member.name}-${index}`}
            className="col-6 col-md-4 col-lg-3 d-flex"
          >

            <button
              type="button"
              className="team2-gallery-card w-100"
              onClick={(event) => onSelect(member, event)}
              aria-label={`View profile for ${member.name}`}
            >

              {/* Profile Image */}
              <div className="team2-gallery-image">
                <img
                  src={`/image/${member.image}`}
                  alt={member.name}
                  loading="lazy"
                />
              </div>

              {/* Profile Information */}
              <div className="team2-gallery-info">

                <h4 className="team2-gallery-name">
                  {member.name}
                </h4>

                {member.role && (
                  <p className="team2-gallery-role">
                    {member.role}
                  </p>
                )}

                <div className="team2-gallery-action">
                  View Profile
                  <i
                    className="fas fa-arrow-right ms-2"
                    aria-hidden="true"
                  />
                </div>

              </div>

            </button>

          </div>
        ))}

      </div>
    </div>
  );
}

// =========================================
// MAIN COMPONENT
// =========================================

export default function Team2() {
  const [teamData, setTeamData] = useState(null);
  const [selectedMember, setSelectedMember] = useState(null);
  const [error, setError] = useState("");

  const closeButtonRef = useRef(null);
  const triggerRef = useRef(null);
  const modalRef = useRef(null);

  // =========================================
  // LOAD TEAM DATA
  // =========================================

  useEffect(() => {
    const controller = new AbortController();

    fetch("/data/team2.json", {
      signal: controller.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        return res.json();
      })
      .then((data) => {
        setTeamData(data);
        setError("");
      })
      .catch((err) => {
        if (err.name === "AbortError") return;

        console.error("Failed to load team2.json:", err);
        setError("Unable to load team information.");
      });

    return () => controller.abort();
  }, []);

  // =========================================
  // OPEN / CLOSE POPUP
  // =========================================

  const openMember = (member, event) => {
    triggerRef.current = event.currentTarget;
    setSelectedMember(member);
  };

  const closeMember = () => {
    setSelectedMember(null);
  };

  // =========================================
  // MODAL BEHAVIOUR
  // =========================================

  useEffect(() => {
    if (!selectedMember) return;

    const previousOverflow = document.body.style.overflow;

    document.body.style.overflow = "hidden";

    closeButtonRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        closeMember();
        return;
      }

      if (event.key !== "Tab") return;

      const modal = modalRef.current;
      if (!modal) return;

      const focusable = Array.from(
        modal.querySelectorAll(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      );

      if (!focusable.length) {
        event.preventDefault();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (
        event.shiftKey &&
        document.activeElement === first
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        document.activeElement === last
      ) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener("keydown", handleKeyDown);

      requestAnimationFrame(() => {
        triggerRef.current?.focus();
      });
    };
  }, [selectedMember]);

  // =========================================
  // ERROR
  // =========================================

  if (error) {
    return (
      <section id="team2" className="team2-section">
        <p className="team2-status team2-error">
          {error}
        </p>
      </section>
    );
  }

  // =========================================
  // LOADING
  // =========================================

  if (!teamData) {
    return (
      <section id="team2" className="team2-section">
        <p className="team2-status">
          Loading team...
        </p>
      </section>
    );
  }

  // =========================================
  // RENDER
  // =========================================

  return (
    <section
      id="team2"
      className="team2-section"
    >
      <div className="container">

        {/* Heading */}
        <div className="team2-header">

          <h2 className="team2-title">
            Meet The Team
          </h2>

          <p className="team2-theme">
            The People Behind TEDxSetapak 2026
          </p>

          <p className="team2-subtitle">
            Meet the passionate individuals bringing
            T.U.M.B.U.H to life.
          </p>

        </div>

        {/* Leads & Curators */}
        <TeamRow
          title="Leads & Curators"
          members={teamData.leadCurators}
          onSelect={openMember}
        />

        {/* Creative & Tech */}
        <TeamRow
          title="Creative & Tech"
          members={teamData.creativeTeam}
          onSelect={openMember}
        />

        {/* Production & Events */}
        <TeamRow
          title="Production & Events"
          members={teamData.productionTeam}
          onSelect={openMember}
        />

      </div>

      {/* =====================================
          MEMBER POPUP
      ===================================== */}

      {selectedMember && (
        <div
          className="team2-modal-backdrop"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              closeMember();
            }
          }}
        >
          <div
            ref={modalRef}
            className="team2-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="team2-modal-title"
          >

            {/* =====================================
                MODAL HEADER
            ===================================== */}

            <div className="team2-modal-header">

              <h4
                id="team2-modal-title"
                className="team2-modal-title"
              >
                {selectedMember.name}
              </h4>

              <button
                ref={closeButtonRef}
                type="button"
                className="team2-modal-close"
                onClick={closeMember}
                aria-label="Close team member profile"
              >
                &times;
              </button>

            </div>

            {/* =====================================
                MODAL BODY
            ===================================== */}

            <div className="team2-modal-body">

              {/* =====================================
                  PROFILE
              ===================================== */}

              <div className="team2-modal-profile">

                {/* Profile Photo */}
                <img
                  src={`/image/${selectedMember.image}`}
                  alt={selectedMember.name}
                  className="team2-modal-photo"
                />

                {/* Profile Information */}
                <div className="team2-modal-profile-info">

                  {/* Name */}
                  <h3 className="team2-modal-name">
                    {selectedMember.name}
                  </h3>

                  {/* Role */}
                  {selectedMember.role && (
                    <p className="team2-modal-role">
                      {selectedMember.role}
                    </p>
                  )}

                  {/* Career */}
                  {selectedMember.career && (
                    <p className="team2-modal-career">
                      {selectedMember.career}
                    </p>
                  )}

                  {/* Social Media */}
                  <SocialLinks member={selectedMember} />

                </div>

              </div>

              {/* =====================================
                  EDUCATION
              ===================================== */}

              {selectedMember.education && (
                <div className="team2-modal-section">
                  <h5>Education & Background</h5>
                  <p>{selectedMember.education}</p>
                </div>
              )}

              {/* =====================================
                  DESCRIPTION
              ===================================== */}

              {selectedMember.description && (
                <div className="team2-modal-section">
                  <h5>About</h5>
                  <p>{selectedMember.description}</p>
                </div>
              )}

              {/* =====================================
                  FUN FACT
              ===================================== */}

              {selectedMember.funFact && (
                <div className="team2-modal-section">
                  <h5>Fun Fact</h5>
                  <p>{selectedMember.funFact}</p>
                </div>
              )}

              {/* =====================================
                  QUOTE
              ===================================== */}

              {selectedMember.quote && (
                <div className="team2-modal-section">
                  <h5>Personal Quote</h5>
                  <p className="team2-modal-quote">
                    “{selectedMember.quote}”
                  </p>
                </div>
              )}

            </div>

            {/* =====================================
                MODAL FOOTER
            ===================================== */}

            <div className="team2-modal-footer">

              <button
                type="button"
                className="team2-footer-button"
                onClick={closeMember}
              >
                Close
              </button>

            </div>

          </div>
        </div>
      )}

    </section>
  );
}